import { z } from "zod";

/**
 * Runtime configuration, written to /config.json by the PortalStack so a
 * single build serves every environment. The mock flag is a build-time
 * switch (`VITE_MOCK=1`): local dev and e2e use MSW and a fake login.
 */
export const ConfigSchema = z.object({
  apiBaseUrl: z.string().url(),
  cognito: z.object({ issuer: z.string().url(), clientId: z.string(), domain: z.string() }),
  environment: z.enum(["dev", "prod", "mock"]),
  /**
   * ADR-0014: SHA-256 of each email domain that signs in through its own
   * identity provider. Hashed so the file does not list customers; the
   * login page hashes the domain typed and routes on a match.
   */
  ssoDomainHashes: z.array(z.string()).default([]),
});
export type AppConfig = z.infer<typeof ConfigSchema>;

export const isMock = import.meta.env.VITE_MOCK === "1";

let cached: AppConfig | undefined;

export async function loadConfig(): Promise<AppConfig> {
  if (cached) return cached;
  if (isMock) {
    cached = {
      apiBaseUrl: "/api",
      cognito: { issuer: "https://mock.invalid", clientId: "mock", domain: "mock.invalid" },
      environment: "mock",
      // sha256("sso.example"): the e2e's federated domain.
      ssoDomainHashes: ["737119dc4b0b9c5e33506406ea32bc6c048c3f20906cafa6f1347126d7d020ab"],
    };
    return cached;
  }
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`config.json returned ${res.status}`);
  cached = ConfigSchema.parse(await res.json());
  return cached;
}
